// Login splash lines. A holiday that is coming up usually wins, then the season, then the everyday mix.

const EVERYDAY = [
  'Welcome back.',
  'Hi there.',
  'Good to see you.',
  'Hey, you.',
  'There you are.',
  'The studio’s all yours.',
  'Ready when you are.',
  'Let’s make something.',
  'The It Girl is in.',
  'Look who’s back.',
  'Hi gorgeous.',
  'Fresh page, fresh ideas.',
  'What are we wearing today?',
  'What’s the trend today?',
  'Your inbox club is waiting.',
  'New story energy.',
  'Coffee in hand? Let’s go.',
  'Main character mode: on.',
  'Back in the studio.',
  'Somebody’s been busy.',
  'Let’s give them something to read.',
  'Big ideas only.',
  'Ready for your close-up?',
  'The edit starts now.',
  'Let’s make it a good one.',
  'Nice to have you back.',
  'Typing in heels again?',
  'The commas missed you.',
  'Wi-Fi strong. Outfit stronger.',
  'Hydrated? Moisturized? Publishing?',
  'Delete nothing. Draft everything.',
  'Your drafts have been gossiping.',
  'Plot twist: you’re early.',
  'Shh. The trends are sleeping.',
  'Matcha first, deadlines later.',
  'Serving looks and paragraphs.',
  'Pretending this is a photoshoot.',
  'Spell check is nervous.',
  'Unread notifications: emotionally.',
  'Brb, rehearsing your acceptance speech.',
  'Don’t worry, nobody saw that typo.',
]

function timeOfDay(hour: number) {
  if (hour < 5) return ['Burning the midnight oil?', 'Late-night edit?']
  if (hour < 12) return ['Good morning.', 'Morning. Coffee first.']
  if (hour < 17) return ['Good afternoon.', 'Afternoon edit?']
  return ['Good evening.', 'Evening. Let’s wrap the day.']
}

const SEASONS = {
  winter: [
    'Cozy knits, hot takes.',
    'Scarf on, drafts open.',
    'It’s cold out. The edit is hot.',
    'Hot cocoa and headlines.',
    'Layer up. Write it up.',
    'Winter arc, editorial edition.',
    'Too cold to go out. Perfect for writing.',
  ],
  spring: [
    'Spring cleaning: start with the drafts.',
    'Fresh blooms, fresh posts.',
    'Florals for spring? Groundbreaking.',
    'Trench coat weather. Trend report weather.',
    'Something’s blooming. Hopefully the traffic.',
    'Out with the old, in with the new season.',
    'April showers, May publishing.',
  ],
  summer: [
    'SPF on. Posts out.',
    'Hot girl summer, hot girl drafts.',
    'Out of office? Not today.',
    'Iced coffee, cool ideas.',
    'Linen season. Deadline season.',
    'Sun’s out, stories out.',
    'Writing from the beach in spirit.',
  ],
  autumn: [
    'Pumpkin spice and everything nice.',
    'Sweater weather, better weather.',
    'Falling for the new edit.',
    'Leaves are falling. Traffic isn’t.',
    'Cozy season, content season.',
    'Boots on. Let’s write.',
    'Back-to-school energy, minus the homework.',
  ],
}

type Season = keyof typeof SEASONS

function season(month: number): Season {
  if (month === 11 || month <= 1) return 'winter'
  if (month <= 4) return 'spring'
  if (month <= 7) return 'summer'
  return 'autumn'
}

const DAY = 24 * 60 * 60 * 1000

function dayStart(year: number, month: number, day: number) {
  return new Date(year, month, day).getTime()
}

/** The nth weekday (0 = Sunday) of a month, e.g. the 4th Thursday of November. */
function nthWeekday(year: number, month: number, weekday: number, n: number) {
  const first = new Date(year, month, 1).getDay()
  return dayStart(year, month, 1 + ((weekday - first + 7) % 7) + (n - 1) * 7)
}

/** Western Easter Sunday (anonymous Gregorian algorithm). */
function easter(year: number) {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31) - 1
  const day = ((h + l - 7 * m + 114) % 31) + 1
  return dayStart(year, month, day)
}

type Holiday = {
  name: string
  lines: string[]
  /** [first day, last day] the lines can show, for the given year. */
  window: (year: number) => [number, number]
}

const HOLIDAYS: Holiday[] = [
  {
    name: 'christmas',
    window: (y) => [dayStart(y, 11, 1), dayStart(y, 11, 26)],
    lines: [
      'Sleigh the edit.',
      'All I want for Christmas is page views.',
      'Deck the drafts.',
      'Making a list, checking the SEO twice.',
      'Merry and bright, and on deadline.',
      'Tinsel on, typos off.',
      'Santa’s watching your word count.',
      'It’s giving festive.',
      'Rockin’ around the content calendar.',
      'Gift guide season. You’ve got this.',
    ],
  },
  {
    name: 'new-year',
    window: (y) => [dayStart(y, 11, 27), dayStart(y + 1, 0, 3)],
    lines: [
      'New year, new drafts.',
      'Resolution: publish more. Starting now.',
      'Champagne problems, editorial solutions.',
      'Out with the old posts, in with the new.',
      'Fresh calendar, who dis?',
      'Last call for this year’s best-of list.',
    ],
  },
  {
    name: 'valentines',
    window: (y) => [dayStart(y, 1, 7), dayStart(y, 1, 14)],
    lines: [
      'Roses are red, drafts are due.',
      'Be mine, dear reader.',
      'Love is in the headlines.',
      'Galentine’s plans? After this post.',
      'Single, taken, or publishing?',
      'Writing love letters to the algorithm.',
    ],
  },
  {
    name: 'st-patricks',
    window: (y) => [dayStart(y, 2, 14), dayStart(y, 2, 17)],
    lines: ['Feeling lucky? Hit publish.', 'Wearing green, writing gold.', 'May your traffic be ever green.'],
  },
  {
    name: 'easter',
    window: (y) => [easter(y) - 6 * DAY, easter(y) + DAY],
    lines: [
      'Hop to it.',
      'Some bunny’s got a deadline.',
      'Egg-cellent timing.',
      'Hunting for the perfect headline.',
      'Pastels: approved. Drafts: pending.',
    ],
  },
  {
    name: 'mothers-day',
    window: (y) => [nthWeekday(y, 4, 0, 2) - 6 * DAY, nthWeekday(y, 4, 0, 2)],
    lines: ['Call your mom. Then publish.', 'Mother’s Day gift guide, anyone?', 'Brunch reservations and deadlines.'],
  },
  {
    name: 'fourth-of-july',
    window: (y) => [dayStart(y, 6, 1), dayStart(y, 6, 4)],
    lines: ['Red, white and on deadline.', 'Fireworks tonight. Hot takes now.', 'Declaring independence from writer’s block.'],
  },
  {
    name: 'halloween',
    window: (y) => [dayStart(y, 9, 20), dayStart(y, 9, 31)],
    lines: [
      'Boo. Back to work.',
      'Something wicked this way writes.',
      'Costume ready? Drafts aren’t.',
      'Spooky season, spicy content.',
      'The only scary thing here is the word count.',
      'Witch better have my drafts.',
      'Trick or tweet.',
    ],
  },
  {
    name: 'thanksgiving',
    window: (y) => [nthWeekday(y, 10, 4, 4) - 6 * DAY, nthWeekday(y, 10, 4, 4)],
    lines: [
      'Thankful for you. And autosave.',
      'Gobble up some drafts.',
      'Pass the stuffing and the edits.',
      'Grateful, caffeinated, publishing.',
      'Pre-pie productivity.',
    ],
  },
  {
    name: 'black-friday',
    window: (y) => [nthWeekday(y, 10, 4, 4) + DAY, nthWeekday(y, 10, 4, 4) + 4 * DAY],
    lines: ['Add to cart: one great post.', 'Doorbusters, but make it editorial.', 'Shop the edit. Write the edit.'],
  },
]

/** The holidays whose window includes `now`, checking last year's windows too (New Year spans two years). */
export function activeHolidays(now: Date): Holiday[] {
  const today = dayStart(now.getFullYear(), now.getMonth(), now.getDate())
  return HOLIDAYS.filter((holiday) =>
    [now.getFullYear(), now.getFullYear() - 1].some((year) => {
      const [start, end] = holiday.window(year)
      return today >= start && today <= end
    }),
  )
}

const HOLIDAY_CHANCE = 0.7
const SEASON_CHANCE = 0.35

function pick<T>(items: T[], random: () => number) {
  return items[Math.floor(random() * items.length)]
}

/** One greeting for this moment. `random` is injectable so the choice can be tested. */
export function pickGreeting(now = new Date(), random: () => number = Math.random) {
  const holidays = activeHolidays(now)
  if (holidays.length && random() < HOLIDAY_CHANCE) return pick(holidays.flatMap((holiday) => holiday.lines), random)
  if (random() < SEASON_CHANCE) return pick(SEASONS[season(now.getMonth())], random)
  return pick([...EVERYDAY, ...timeOfDay(now.getHours())], random)
}
